<template>
    <div class="forest-disturbances-map-container">
        <div class="map-wrapper">
            <mapbox-gl-map
                id="forest-disturbances-map"
                :options="mapOptions"
                zoom-control
                @load="onMapLoad"
                @error="onMapError"
            />
            <style-changer
                :selected="mapStyle.label"
                :options="mapStyles"
                @change="changeMapStyle"
            />
        </div>
        <fs-legend
            :items="legendItems"
            :items-clickable="false"
        />
    </div>
</template>

<script>
import FsLegend from '@foundryspatial/legend';
import mapStyleSwitcherMixin from '@foundryspatial/map-style-changer/mixin';
import MapboxGlMap from '@foundryspatial/mapbox-gl-map';
import bbox from '@turf/bbox';
import pointsWithinPolygon from '@turf/points-within-polygon';

import MapPolygon from '@/assets/legend-items/MapPolygon.vue';
import scssVars from '@/assets/scss/_variables.scss';
import { EQUIVALENT_CLEARCUT_AREA as MAP_STYLES } from '@/constants/mapStyles.js';
import { API_CRITICAL_ERROR } from '@/constants/messages.js';

export default {
    components: {
        MapboxGlMap,
        FsLegend,
    },

    mixins: [
        mapStyleSwitcherMixin,
    ],

    props: {
        watershed: {
            type: Object,
            required: true,
        },

        disturbances: {
            type: Array,
            required: false,
            default: () => [],
        },

        h60Areas: {
            type: Object,
            required: false,
            default: () => ({}),
        },
    },

    data: () => ({
        map: null,
        selectedForestDisturbances: [],

        mapStyles: [
            {
                label: 'Base Map',
                styleUrl: MAP_STYLES.BASE.URL,
            },
            {
                label: 'Satellite',
                styleUrl: MAP_STYLES.SATELLITE.URL,
            },
        ],

        pointLayers: ['change-points-2012', 'change-points-2005', 'change-points-1985'],
    }),

    computed: {
        mapOptions() {
            return {
                style: MAP_STYLES.BASE.URL,
                bounds: this.mapBounds,
                scrollZoom: false,
                fitBoundsOptions: {
                    padding: 30,
                },
            };
        },

        mapBounds() {
            return bbox(this.watershed.geom);
        },

        legendItems() {
            return [
                {
                    label: 'Selected Watershed',
                    icon: MapPolygon,
                    iconFillColor: `${scssVars.colorWatershedQuery}80`,
                    iconStrokeColor: scssVars.colorWatershedQueryBorder,
                },
                {
                    label: 'H60 Areas',
                    icon: MapPolygon,
                    iconFillColor: scssVars.colorH60AreasLegendFill,
                    iconStrokeColor: scssVars.colorH60AreasLegend,
                },
                {
                    iconShape: 'circle',
                    label: 'Fire Disturbance',
                    iconFillColor: scssVars.colorFireDisturbance,
                },
                {
                    iconShape: 'circle',
                    label: 'Infrastructure Disturbance',
                    iconFillColor: scssVars.colorInfrastructureDisturbance,
                },
                {
                    iconShape: 'circle',
                    label: 'Harvest Disturbance',
                    iconFillColor: scssVars.colorHarvestDisturbance,
                },
                {
                    iconShape: 'circle',
                    label: 'Mountain Pine Beetle Disturbance',
                    iconFillColor: scssVars.colorMountainPineBeetleDisturbance,
                },
            ];
        },
    },

    watch: {
        disturbances() {
            if (this.map) {
                this.showDisturbancesWithinWatershed();
            }
        },
    },

    methods: {
        /**
         * Figure out which forest disturbance points are within the selected watershed
         */
        getForestDisturbancePoints() {
            const projectedBounds = [
                this.map.project([this.mapBounds[0], this.mapBounds[1]]),
                this.map.project([this.mapBounds[2], this.mapBounds[3]]),
            ];

            const features = this.map.queryRenderedFeatures(projectedBounds, {
                layers: this.pointLayers,
            });

            // de-dupe features
            const uniqueFeatures = Array.from(new Set(features.map((a) => a.id)))
                .map((id) => features.find((a) => a.id === id));

            const matchedFeatures = pointsWithinPolygon(
                {
                    type: 'FeatureCollection',
                    features: uniqueFeatures,
                },
                this.watershed.geom,
            );

            // assemble each queried forest disturbance point data with its point geometry
            this.selectedForestDisturbances = matchedFeatures.features.map((feature) => ({
                ...feature.properties,
                geometry: feature._geometry,
                id: feature.id,
            }));

            this.$emit('forest-disturbances-data-change', this.selectedForestDisturbances);
        },

        /**
         * Event handler for map "load" event.
         *
         * @param {L.Event} ev - Leaflet event data object
         * @param {L.map} map - Leaflet map object
         */
        onMapLoad(ev, map) {
            this.map = map;

            this.getForestDisturbancePoints();

            this.addWatershedPolygon();
            this.addH60AreaPolygons();
            this.showDisturbancesWithinWatershed();

            this.$emit('map-loaded-zoom', map.getZoom());
        },

        addWatershedPolygon() {
            this.map.addSource('watershed-polygon', {
                type: 'geojson',
                data: {
                    type: 'Feature',
                    geometry: this.watershed.geom,
                },
            });

            this.map.addLayer({
                id: 'watershed-polygon',
                type: 'fill',
                source: 'watershed-polygon',
                paint: {
                    'fill-color': scssVars.colorWatershedQuery,
                    'fill-opacity': 0.5,
                    'fill-outline-color': scssVars.colorWatershedQueryBorder,
                },
            }, this.pointLayers[2]);
        },

        addH60AreaPolygons() {
            // handle cases where no H60 areas exist
            if (!Object.keys(this.h60Areas).length) return;

            this.map.addSource('critical-change-areas-source', {
                type: 'geojson',
                data: {
                    type: 'FeatureCollection',
                    features: [
                        {
                            type: 'Feature',
                            geometry: this.h60Areas,
                        },
                    ],
                },
            });

            this.map.addLayer({
                id: 'critical-change-areas-polygons',
                type: 'fill',
                source: 'critical-change-areas-source',
                paint: {
                    'fill-color': scssVars.colorWatershedQuery,
                    'fill-opacity': 0.5,
                    'fill-outline-color': scssVars.colorWatershedQueryBorder,
                },
            }, this.pointLayers[2]);

            this.map.addLayer({
                id: 'critical-change-areas-polygons-outline',
                type: 'line',
                source: 'critical-change-areas-source',
                paint: {
                    'line-color': scssVars.colorWatershedQueryBorder,
                    'line-opacity': 1,
                    'line-width': 2,
                },
            }, this.pointLayers[2]);
        },

        showDisturbancesWithinWatershed() {
            const ids = this.disturbances.map((disturbance) => disturbance.id);

            if (ids && ids.length) {
                this.pointLayers.forEach((layerId) => {
                    this.map.setFilter(layerId, ['match', ['id'], [...ids], true, false]);
                });
            } else {
                this.pointLayers.forEach((layerId) => {
                    this.map.setFilter(layerId, ['match', ['id'], [-1], true, false]);
                });
            }

            this.pointLayers.forEach((layerId) => {
                this.map.setPaintProperty(layerId, 'circle-opacity', 1);
            });
        },

        /**
         * Pass Mapbox GL errors to global error handler to display a message.
         *
         * @param {Event} errorEvent - Mapbox GL error event object
         */
        onMapError(errorEvent) {
            // pass to error handler as a critical error
            this.$handleError(errorEvent, API_CRITICAL_ERROR, true);
        },

        onMapStyleChange() {
            this.addWatershedPolygon();
            this.addH60AreaPolygons();
            this.showDisturbancesWithinWatershed();
        },
    },
};
</script>

<style lang="scss">
.forest-disturbances-map-container {
    display: grid;
    grid-template-rows: 1fr auto;

    .map-wrapper {
        position: relative;

        #forest-disturbances-map {
            height: 80vh;
            width: 100%;
        }

        // manual adjustments to fix the style changer/zoom control overlaps
        @media (max-width: 1298px), (min-width: 1408px) and (max-width: 1460px) {
            .map-style-changer {
                bottom: 115px;
            }
        }
    }

    .legend-container {
        display: grid;
        grid-template-columns: repeat(2, auto);
    }

    @include tablet {
        .legend-container {
            .legend-item {
                display: grid;
                grid-template-columns: auto 1fr;
            }
        }
    }
}
</style>
